// apps/roleforge-app/src/components/audit/AttributedAssetsPane.tsx
//
// Phase 11 task 11.3 — middle-column pane that takes over when the owner
// clicks a voice in the Voices tab. Renders the pain points and
// opportunities scoped to a single thread (i.e. attributed to one voice)
// with verbatim text and timestamps.

import { useEffect, useState } from 'react';
import {
  getAttributedAssets,
  type AttributedPainPoint,
  type Opportunity,
} from '../../lib/api-client';

interface Props {
  threadId: string;
  voiceLabel: string;
  jwt?: string | null;
}

interface State {
  pain_points: AttributedPainPoint[];
  opportunities: Opportunity[];
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export function AttributedAssetsPane({ threadId, voiceLabel, jwt }: Props) {
  const [state, setState] = useState<State | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState(null);
    setError(null);
    if (!jwt) {
      setError('Missing session');
      return () => {
        cancelled = true;
      };
    }
    void getAttributedAssets(jwt, threadId)
      .then((res) => {
        if (!cancelled) setState(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, threadId]);

  return (
    <div
      data-testid="attributed-assets-pane"
      className="flex h-full min-h-0 flex-col overflow-y-auto px-6 py-4"
    >
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-ink">
          {voiceLabel}'s contributions
        </h2>
        <p className="text-xs text-graphite">
          Verbatim pain points and opportunities attributed to this voice.
        </p>
      </header>

      {error && (
        <p className="text-sm text-coral">
          Couldn't load contributions: {error}
        </p>
      )}

      {!error && state === null && (
        <p className="text-sm text-graphite italic">Loading…</p>
      )}

      {state && (
        <div className="flex flex-col gap-6">
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-graphite">
              Pain points
            </h3>
            {state.pain_points.length === 0 ? (
              <p className="text-sm text-graphite italic">
                No pain points from this voice yet.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {state.pain_points.map((p) => (
                  <li
                    key={p.id}
                    className="rounded-xl border border-line bg-card p-3"
                  >
                    <p className="text-sm text-ink">{p.description}</p>
                    <p className="mt-1 text-[11px] text-graphite">
                      {fmtTime(p.created_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-graphite">
              Opportunities
            </h3>
            {state.opportunities.length === 0 ? (
              <p className="text-sm text-graphite italic">
                No opportunities from this voice yet.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {state.opportunities.map((o) => (
                  <li
                    key={o.id}
                    className="rounded-xl border border-line bg-card p-3"
                  >
                    <p className="text-sm text-ink">{o.name}</p>
                    {o.description && (
                      <p className="mt-1 text-xs text-graphite">
                        {o.description}
                      </p>
                    )}
                    <p className="mt-1 text-[11px] text-graphite">
                      {fmtTime(o.created_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
